import {
  MapContainer,
  Marker,
  Polygon,
  TileLayer,
  useMapEvent,
} from 'react-leaflet';
import 'react-pure-modal/dist/react-pure-modal.min.css';
import 'leaflet/dist/leaflet.css';
import type { LatLngExpression, Map } from 'leaflet';
import { LatLng } from 'leaflet';
import * as turf from '@turf/turf';
import { CRS } from 'leaflet';
import { useEffect, useRef, useState } from 'react';
import { pin } from '../../../pages/home/utils/HomeMapComponent';
import { GeocoderControl } from './GeocoderControl';
import ScrollZoomControl from './ScrollZoomControl';
import { Box, InputLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';

// type Error =
export type ErrorStatus =
  | { tag: 'Submited'; errors: string[] }
  | { tag: 'Not submitted' };

interface CoordianatesPickerProps {
  onClick: (
    value: { lat: number; lng: number },
    area: number,
    addressData: Record<any, any> | undefined
  ) => void;
  label: string;
  defaultValues?: {
    position: { lat: number; lng: number };
    addressData: Record<any, any> | undefined;
  };
  required?: boolean;
  context: string;
  errors: ErrorStatus;
}

export default function CoordianatesPicker({
  defaultValues,
  onClick,
  label,
  required,
  context,
  errors,
}: CoordianatesPickerProps) {
  const [coordinates, setCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(defaultValues?.position ?? null);
  const [addressData, setAddressData] = useState<
    Record<string, string> | undefined
  >(defaultValues?.addressData ?? undefined);

  //use translation here:
  const { t } = useTranslation('commons'); 

  const [radius, setRadius] = useState<number>(0);
  const mapRef = useRef<Map>(null);

  const isSubmited = errors.tag === 'Submited';
  const errorList = isSubmited ? errors.errors : [];

  const className =
    isSubmited && errorList.length > 0
      ? 'form-danger'
      : isSubmited
      ? 'form-success'
      : '';

  /**
   * Handle reverse geocoding to get address from coordinates.
   * @param coords
   * @returns
   */
  const reverseGeocode = async (coords: { lat: number; lng: number }) => {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${coords.lat}&lon=${coords.lng}&format=json&addressdetails=1`;
    const res = await fetch(url);
    return await res.json();
  };

  /**
   * On map click.
   */
  const ClickHandler = () => {
    useMapEvent('click', (e) => {
      const coord = e.latlng;
      const lat = coord.lat;
      const lng = coord.lng;

      setCoordinates({ lat: lat, lng: lng });

      reverseGeocode({ lat: lat, lng: lng }).then((data) => {
        const address = data.address;
        setAddressData(address);
        handleCoordinates({ lat: lat, lng: lng }, address);
      });
    });
    return null;
  };

  const handleCoordinates = (
    coordinates: { lat: number; lng: number },
    addressData: Record<any, any> | undefined
  ) => {
    setCoordinates(coordinates);
    setAddressData(addressData);
    onClick(coordinates, Math.round(turf.area(polygonFeature)), addressData);
    if (coordinates) {
      onClick(coordinates, Math.round(turf.area(polygonFeature)), addressData);
    }
  };

  const bufferizeRectangle = (width: number, height: number): number[][][] => {
    if (coordinates) {
      // Construct array of coordinates.
      const point = [coordinates.lng, coordinates.lat];

      // Calculate width and height.
      const halfWidth = width / 2 / 500;
      const halfHeight = height / 2 / 250;

      // Rectangle's offsest.
      // @see https://turfjs.org/ (lat/ lng coordinates on turf must be inversed).
      const bbox: [number, number, number, number] = [
        point[1]! - halfWidth,
        point[0]! - halfHeight,
        point[1]! + halfWidth,
        point[0]! + halfHeight,
      ];

      // Create rectangle with turf.
      const rectangleEPSG3857 = turf.bboxPolygon(bbox);
      return rectangleEPSG3857.geometry.coordinates;
    }
    return [];
  };

  // Render with selected width.
  const markerBufferRectangle = bufferizeRectangle(radius, radius * 0.809);
  const polygonFeature = turf.polygon(markerBufferRectangle);

  function handleView() {
    if (mapRef.current && coordinates) {
      mapRef.current.flyTo([coordinates.lat, coordinates.lng], 14, {
        animate: true,
        duration: 1,
      });
    }
  }

  function findAdresseFromPosition(
    addressData: Record<string, string> | undefined
  ) {
    if (addressData) {
      return [
        addressData.house_number,
        addressData.road,
        addressData.postcode,
        addressData.city ?? addressData.town,
      ]
        .filter((item) => !!item)
        .map((item) =>
          String(item)
            .replace(/\bundefined\b\s*/gi, '')
            .replace(/\bnull\b\s*/gi, '')
        )
        .join(' ')
        .trim();
    }
    return t('Aucune adresse trouvée');
  }

  useEffect(() => {
    handleView();
  }, [coordinates]);

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid rgba(0, 0, 0, 0.23)', // bordure MUI
        transition: 'border-color 0.2s ease-in-out',
        '&:hover': {
          borderColor: 'rgba(0, 0, 0, 0.87)', // hover MUI
        },
        '&:focus-within': {
          borderColor: '#1976d2', // focus MUI bleu
          boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
        },
      }}
    >
      <InputLabel className="uk-form-label" sx={{ padding: '16.5px 14px' }}>
        {label} {required && <span>*</span>}
      </InputLabel>
      <div className={className} style={{ padding: '0px 14px' }}>
        <MapContainer
          center={[
            coordinates ? coordinates.lat : 46.651522,
            coordinates ? coordinates.lng : 2.390475,
          ]}
          zoom={coordinates ? 15 : 5}
          style={{ height: '300px', width: '100%' }}
          crs={CRS.EPSG3857}
          ref={mapRef}
          zoomControl={true}
          scrollWheelZoom={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickHandler />
          <Marker
            position={new LatLng(coordinates?.lat ?? 0, coordinates?.lng ?? 0)}
            icon={pin}
          />
          <Polygon positions={markerBufferRectangle as LatLngExpression[][]} />;
          <GeocoderControl onFound={handleCoordinates} />
          <ScrollZoomControl />
        </MapContainer>
      </div>
      {coordinates && (
        <InputLabel className="uk-form-label" sx={{ padding: '16.5px 14px' }}>
          {`${t('Adresse')} ${context}: ${findAdresseFromPosition(addressData)}`}
        </InputLabel>
      )}
      {isSubmited && errorList.length > 0 && (
        <div style={{ fontSize: '13px' }}>
          {errorList.map((err, index) => (
            <p
              key={index}
              style={{ color: 'red', margin: '0', marginTop: '5px' }}
              className="form-danger warning"
            >
              <span uk-icon="icon: warning; ratio: 0.8"></span> {err}
            </p>
          ))}
        </div>
      )}
    </Box>
  );
}
